/**
 * Core-100 R3 — CORE-BH: how promises in trouble turned out (`due_out_mitigation_outcomes`, R2-66; managers only).
 * A rate is shown only over promises that have an outcome — never a percentage of nothing.
 */
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { describeDueOutOutcomes, describeWaiterPromises, type DueOutOutcomes, type WaiterPromiseSummary } from '@/lib/operations/attention';

// A member call, never a detached `supabase.rpc` (a detached method loses its client and throws before any request).
type LooseRpc = { rpc: (fn: string, args: Record<string, unknown>) => Promise<{ data: unknown; error: { message: string } | null }> };
const rpc = (fn: string, args: Record<string, unknown>) => (supabase as unknown as LooseRpc).rpc(fn, args);

export function DueOutOutcomesCard({ shopId, days = 30 }: { shopId: string; days?: number }) {
  const q = useQuery({
    queryKey: ['due-out-outcomes', shopId, days],
    queryFn: async (): Promise<DueOutOutcomes | null> => {
      const { data, error } = await rpc('due_out_mitigation_outcomes', { p_shop_id: shopId, p_days: days });
      if (error) throw error;
      return ((data ?? []) as DueOutOutcomes[])[0] ?? null;
    },
  });
  // Core-100 R4 CORE-BG: the waiting customers' promise (waiter_promise_outcomes had no reader).
  const w = useQuery({
    queryKey: ['waiter-promise-summary', shopId, days],
    queryFn: async (): Promise<WaiterPromiseSummary | null> => {
      const { data, error } = await rpc('waiter_promise_summary', { p_shop_id: shopId, p_days: days });
      if (error) throw error;
      return ((data ?? []) as WaiterPromiseSummary[])[0] ?? null;
    },
  });
  return (
    <Card data-testid="due-out-outcomes-card">
      <CardHeader>
        <CardTitle>Promises in trouble</CardTitle>
        <CardDescription>Due-out promises the server found at risk in the last {days} days, and how they ended.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {q.isLoading && <p className="text-muted-foreground">Loading…</p>}
        {q.isError && <p className="text-destructive">These outcomes could not be loaded.</p>}
        {q.isSuccess && (
          <>
            <p>{describeDueOutOutcomes(q.data)}</p>
            {q.data && Number(q.data.detected) > 0 && (
              <p className="text-xs text-muted-foreground">
                At risk {q.data.detected_at_risk} · already missed when found {q.data.detected_missed} · still open {q.data.open} · cancelled {q.data.cancelled}
                {Number(q.data.re_promised ?? 0) > 0 ? ` · re-promised ${q.data.re_promised}` : ''}
              </p>
            )}
          </>
        )}
        <div className="border-t pt-2" data-testid="waiter-promise-summary">
          <p className="font-medium">Waiting customers</p>
          {w.isLoading && <p className="text-muted-foreground">Loading…</p>}
          {w.isError && <p className="text-destructive">The waiting customers' promises could not be loaded.</p>}
          {w.isSuccess && <p>{describeWaiterPromises(w.data)}</p>}
        </div>
      </CardContent>
    </Card>
  );
}
